import React, { useEffect, useRef, useState } from 'react';
import DealerLayout from '../components/DealerLayout';
import api from '../api/axios';
import { C } from '../theme';

/* ── Animated counter ─────────────────────────────────────────────────── */
function Counter({ to, prefix = '', suffix = '', duration = 900 }) {
  const [val, setVal] = useState(0);
  const raf = useRef(null);
  useEffect(() => {
    const start = Date.now();
    const tick = () => {
      const p = Math.min((Date.now() - start) / duration, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      setVal(to * ease);
      if (p < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [to, duration]);
  return <>{prefix}{Math.round(val).toLocaleString('en-IN')}{suffix}</>;
}

/* ── Bezier area chart ────────────────────────────────────────────────── */
function AreaChart({ data, color = '#7C3AED', height = 140 }) {
  if (!data || data.length < 2) return null;
  const W = 600, H = height;
  const max = Math.max(...data.map(d => d.amount), 1);
  const pts = data.map((d, i) => ({
    x: (i / (data.length - 1)) * W,
    y: H - 16 - ((d.amount / max) * (H - 32)),
  }));
  let path = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 1; i < pts.length; i++) {
    const cx = (pts[i - 1].x + pts[i].x) / 2;
    path += ` C ${cx} ${pts[i - 1].y} ${cx} ${pts[i].y} ${pts[i].x} ${pts[i].y}`;
  }
  const area = `${path} L ${pts[pts.length - 1].x} ${H} L ${pts[0].x} ${H} Z`;
  const gid = `ddg-${color.replace('#', '')}`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: `${height}px` }} preserveAspectRatio="none">
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gid})`} />
      <path d={path} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      {pts.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r="4" fill={color} stroke="#fff" strokeWidth="2" />
      ))}
    </svg>
  );
}

/* ── Donut chart ──────────────────────────────────────────────────────── */
function Donut({ segments, size = 140 }) {
  const r = 54, cx = 70, cy = 70;
  const circ = 2 * Math.PI * r;
  const total = segments.reduce((s, x) => s + x.value, 0) || 1;
  let offset = 0;
  return (
    <svg width={size} height={size} viewBox="0 0 140 140">
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#F3F4F6" strokeWidth="20" />
      {segments.map((seg, i) => {
        const dash = (seg.value / total) * circ;
        const gap  = circ - dash;
        const el = (
          <circle
            key={i} cx={cx} cy={cy} r={r}
            fill="none" stroke={seg.color} strokeWidth="20"
            strokeDasharray={`${dash} ${gap}`}
            strokeDashoffset={-offset}
            transform="rotate(-90 70 70)"
          />
        );
        offset += dash;
        return el;
      })}
      <text x={cx} y={cy - 6} textAnchor="middle" fontSize="18" fontWeight="800" fill="#1F2937">
        {total.toLocaleString('en-IN')}
      </text>
      <text x={cx} y={cy + 12} textAnchor="middle" fontSize="10" fill="#6B7280">Total</text>
    </svg>
  );
}

/* ── Ring progress ────────────────────────────────────────────────────── */
function Ring({ pct, color, size = 60 }) {
  const r = 22, circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  return (
    <svg width={size} height={size} viewBox="0 0 56 56">
      <circle cx="28" cy="28" r={r} fill="none" stroke="#F3F4F6" strokeWidth="6" />
      <circle cx="28" cy="28" r={r} fill="none" stroke={color} strokeWidth="6"
        strokeDasharray={`${dash} ${circ - dash}`} strokeDashoffset={circ * 0.25}
        strokeLinecap="round" transform="rotate(-90 28 28)" />
      <text x="28" y="32" textAnchor="middle" fontSize="10" fontWeight="700" fill={color}>{pct}%</text>
    </svg>
  );
}

/* ── KPI card ─────────────────────────────────────────────────────────── */
function KpiCard({ icon, label, value, sub, color, bg, border }) {
  return (
    <div style={{
      background: bg, border: `1.5px solid ${border}`,
      borderRadius: '16px', padding: '20px 22px',
      display: 'flex', flexDirection: 'column', gap: '8px',
    }}>
      <div style={{ fontSize: '26px' }}>{icon}</div>
      <div style={{ fontSize: '10px', fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.6px' }}>{label}</div>
      <div style={{ fontSize: '24px', fontWeight: 800, color }}>{value}</div>
      {sub && <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '-2px' }}>{sub}</div>}
    </div>
  );
}

/* ── Main ─────────────────────────────────────────────────────────────── */
export default function DealerDashboard() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/api/dealer/dashboard')
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const fmtK = n => {
    const v = Number(n || 0);
    if (v >= 10000000) return `₹${(v / 10000000).toFixed(1)}Cr`;
    if (v >= 100000)   return `₹${(v / 100000).toFixed(1)}L`;
    return `₹${v.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
  };
  const fmt = n => '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });

  if (loading) return (
    <DealerLayout title="Dashboard">
      <div style={{ textAlign: 'center', padding: '80px', color: C.textSub }}>Loading…</div>
    </DealerLayout>
  );

  const dealer  = data?.dealer || {};
  const bal     = data?.balance || 0;
  const inv     = data?.inventory || {};
  const pay     = data?.payments  || {};
  const maps    = data?.mappings  || {};
  const dms     = data?.dealer_mappings || {};
  const ren     = data?.renewals  || {};
  const monthly = data?.monthly_revenue || [];
  const topC    = data?.top_clients     || [];
  const recent  = data?.recent_activity || [];
  const byCat   = inv.by_category || [];

  const maxAmt  = Math.max(...topC.map(c => c.amount), 1);
  const dmPct   = dms.total  ? Math.round((dms.active  / dms.total)  * 100) : 0;
  const mapPct  = maps.total ? Math.round((maps.active / maps.total) * 100) : 0;

  const COLORS = ['#7C3AED', '#2563EB', '#059669', '#D97706', '#DC2626'];

  return (
    <DealerLayout title="Dashboard">

      {/* ── Hero banner ──────────────────────────────────────────────── */}
      <div style={{
        background: 'linear-gradient(135deg, #3B0764 0%, #6D28D9 55%, #1E1040 100%)',
        borderRadius: '20px', padding: '28px 32px', marginBottom: '24px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: '20px', position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', width: '300px', height: '300px', borderRadius: '50%', background: 'rgba(255,255,255,0.04)', top: '-80px', right: '80px', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', width: '180px', height: '180px', borderRadius: '50%', background: 'rgba(255,255,255,0.04)', bottom: '-60px', right: '280px', pointerEvents: 'none' }} />

        <div style={{ position: 'relative' }}>
          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '6px' }}>
            {dealer.dealer_id} · Dealer Portal
          </div>
          <div style={{ fontSize: '24px', fontWeight: 800, color: '#fff', marginBottom: '4px' }}>
            Welcome, {dealer.company_name || 'Dealer'} 👋
          </div>
          <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>
            {data?.today}
          </div>
        </div>

        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', position: 'relative' }}>
          <div style={{
            background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(10px)',
            borderRadius: '14px', padding: '16px 22px', border: '1px solid rgba(255,255,255,0.12)',
            minWidth: '130px',
          }}>
            <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>Wallet Balance</div>
            <div style={{ fontSize: '20px', fontWeight: 800, color: bal >= 0 ? '#A7F3D0' : '#FCA5A5' }}>{fmt(bal)}</div>
          </div>
          <div style={{
            background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(10px)',
            borderRadius: '14px', padding: '16px 22px', border: '1px solid rgba(255,255,255,0.12)',
            minWidth: '130px',
          }}>
            <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>This Month</div>
            <div style={{ fontSize: '20px', fontWeight: 800, color: '#FDE68A' }}>{fmtK(pay.this_month)}</div>
          </div>
          <div style={{
            background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(10px)',
            borderRadius: '14px', padding: '16px 22px', border: '1px solid rgba(255,255,255,0.12)',
            minWidth: '130px',
          }}>
            <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>My Clients</div>
            <div style={{ fontSize: '20px', fontWeight: 800, color: '#C4B5FD' }}>
              <Counter to={data?.clients || 0} />
            </div>
          </div>
        </div>
      </div>

      {/* ── KPI cards ─────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(185px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        <KpiCard icon="🗂️" label="Dealer Mappings" color="#6D28D9" bg="#F5F3FF" border="#DDD6FE"
          value={<Counter to={dms.total || 0} />}
          sub={`${dms.active || 0} active`}
        />
        <KpiCard icon="💳" label="Credit Mappings" color="#1D4ED8" bg="#EFF6FF" border="#BFDBFE"
          value={<Counter to={maps.total || 0} />}
          sub={`${maps.active || 0} active · ${maps.completed || 0} done`}
        />
        <KpiCard icon="📦" label="Dealer Stock" color="#047857" bg="#ECFDF5" border="#6EE7B7"
          value={<Counter to={inv.dealer_stock || 0} />}
          sub={`${inv.installed || 0} installed at clients`}
        />
        <KpiCard icon="💰" label="Total Collected" color="#059669" bg="#ECFDF5" border="#6EE7B7"
          value={fmtK(pay.collected)}
          sub={`${fmtK(pay.pending)} pending`}
        />
        <KpiCard icon="🔄" label="Renewals" color="#D97706" bg="#FFFBEB" border="#FDE68A"
          value={<Counter to={ren.total || 0} />}
          sub={`${ren.this_month || 0} this month · ${ren.completed || 0} done`}
        />
        <KpiCard icon="👥" label="Total Clients" color="#7C3AED" bg="#F5F3FF" border="#DDD6FE"
          value={<Counter to={data?.clients || 0} />}
          sub="Registered under you"
        />
      </div>

      {/* ── Revenue chart + Mapping donut ─────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: '16px', marginBottom: '24px' }}>

        <div style={{ background: C.white, borderRadius: '16px', border: `1px solid ${C.border}`, padding: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: '15px', color: C.textMain }}>Revenue Trend</div>
              <div style={{ fontSize: '11px', color: C.textSub, marginTop: '2px' }}>Last 6 months</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '22px', fontWeight: 800, color: '#7C3AED' }}>{fmtK(pay.collected)}</div>
              <div style={{ fontSize: '10px', color: C.textSub }}>Total collected</div>
            </div>
          </div>
          <AreaChart data={monthly} color="#7C3AED" height={130} />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px' }}>
            {monthly.map((m, i) => (
              <div key={i} style={{ textAlign: 'center', flex: 1 }}>
                <div style={{ fontSize: '11px', fontWeight: 700, color: '#7C3AED' }}>
                  {m.amount > 0 ? fmtK(m.amount) : '—'}
                </div>
                <div style={{ fontSize: '10px', color: C.textSub }}>{m.month}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ background: C.white, borderRadius: '16px', border: `1px solid ${C.border}`, padding: '24px' }}>
          <div style={{ fontWeight: 700, fontSize: '15px', color: C.textMain, marginBottom: '16px' }}>Mapping Breakdown</div>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
            <Donut size={140} segments={[
              { value: dms.active    || 0, color: '#7C3AED' },
              { value: maps.active   || 0, color: '#2563EB' },
              { value: maps.completed|| 0, color: '#10B981' },
              { value: ren.total     || 0, color: '#F59E0B' },
            ]} />
          </div>
          {[
            { label: 'DM Active',      val: dms.active     || 0, color: '#7C3AED' },
            { label: 'Credit Active',  val: maps.active    || 0, color: '#2563EB' },
            { label: 'Completed',      val: maps.completed || 0, color: '#10B981' },
            { label: 'Renewals',       val: ren.total      || 0, color: '#F59E0B' },
          ].map(s => (
            <div key={s.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: '9px', height: '9px', borderRadius: '50%', background: s.color }} />
                <span style={{ fontSize: '11px', color: C.textSub }}>{s.label}</span>
              </div>
              <span style={{ fontSize: '13px', fontWeight: 700, color: C.textMain }}>{s.val}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Progress rings + Payment summary ──────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>

        <div style={{ background: C.white, borderRadius: '16px', border: `1px solid ${C.border}`, padding: '24px' }}>
          <div style={{ fontWeight: 700, fontSize: '15px', color: C.textMain, marginBottom: '20px' }}>📊 Activity Overview</div>
          <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <Ring pct={dmPct} color="#7C3AED" size={64} />
              <div>
                <div style={{ fontSize: '11px', color: C.textSub }}>Dealer Mappings</div>
                <div style={{ fontSize: '18px', fontWeight: 800, color: '#7C3AED' }}>{dms.total || 0}</div>
                <div style={{ fontSize: '11px', color: '#7C3AED', fontWeight: 600 }}>{dms.active || 0} active</div>
              </div>
            </div>
            <div style={{ width: '1px', background: C.border }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <Ring pct={mapPct} color="#2563EB" size={64} />
              <div>
                <div style={{ fontSize: '11px', color: C.textSub }}>Credit Mappings</div>
                <div style={{ fontSize: '18px', fontWeight: 800, color: '#2563EB' }}>{maps.total || 0}</div>
                <div style={{ fontSize: '11px', color: '#10B981', fontWeight: 600 }}>{maps.completed || 0} completed</div>
              </div>
            </div>
          </div>
          <div style={{ marginTop: '20px', padding: '14px', borderRadius: '10px', background: '#FFFBEB', border: '1px solid #FDE68A' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: '11px', fontWeight: 600, color: '#D97706' }}>🔄 Renewals This Month</div>
                <div style={{ fontSize: '11px', color: C.textSub, marginTop: '2px' }}>{ren.completed || 0} completed total</div>
              </div>
              <div style={{ fontSize: '26px', fontWeight: 800, color: '#D97706' }}>{ren.this_month || 0}</div>
            </div>
          </div>
        </div>

        <div style={{ background: C.white, borderRadius: '16px', border: `1px solid ${C.border}`, padding: '24px' }}>
          <div style={{ fontWeight: 700, fontSize: '15px', color: C.textMain, marginBottom: '20px' }}>💰 Payment Summary</div>
          {[
            { label: 'Total Collected', val: pay.collected || 0, color: '#059669', bg: '#ECFDF5' },
            { label: 'Pending Amount',  val: pay.pending   || 0, color: '#DC2626', bg: '#FEF2F2' },
            { label: 'This Month',      val: pay.this_month|| 0, color: '#D97706', bg: '#FFFBEB' },
          ].map(r => (
            <div key={r.label} style={{
              background: r.bg, borderRadius: '10px', padding: '14px 16px',
              marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <span style={{ fontSize: '12px', fontWeight: 600, color: C.textSub }}>{r.label}</span>
              <span style={{ fontSize: '16px', fontWeight: 800, color: r.color }}>{fmtK(r.val)}</span>
            </div>
          ))}
          <div style={{ padding: '14px', borderRadius: '10px', background: '#F5F3FF', border: '1.5px solid #DDD6FE', marginTop: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '12px', fontWeight: 600, color: '#6D28D9' }}>📦 Items in Dealer Stock</span>
              <span style={{ fontSize: '18px', fontWeight: 800, color: '#6D28D9' }}>{inv.dealer_stock || 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Inventory by category ──────────────────────────────────────── */}
      {byCat.length > 0 && (
        <div style={{ background: C.white, borderRadius: '16px', border: `1px solid ${C.border}`, padding: '24px', marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: '15px', color: C.textMain }}>📦 Inventory by Category</div>
              <div style={{ fontSize: '11px', color: C.textSub, marginTop: '2px' }}>Dealer stock + installed at clients</div>
            </div>
            <div style={{ display: 'flex', gap: '14px', fontSize: '11px' }}>
              {[{ label: 'Dealer Stock', color: '#7C3AED' }, { label: 'Installed', color: '#059669' }].map(l => (
                <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <div style={{ width: '10px', height: '10px', borderRadius: '2px', background: l.color }} />
                  <span style={{ color: C.textSub }}>{l.label}</span>
                </div>
              ))}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '14px' }}>
            {byCat.map(cat => {
              const maxCat = Math.max(cat.dealer_stock + cat.installed, 1);
              return (
                <div key={cat.name} style={{ border: `1px solid ${C.border}`, borderRadius: '12px', padding: '16px', background: '#FAFAFA' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 700, color: C.textMain }}>{cat.name}</span>
                    <span style={{ fontSize: '11px', fontWeight: 700, color: C.textSub }}>{cat.total}</span>
                  </div>
                  <div style={{ height: '8px', borderRadius: '4px', overflow: 'hidden', background: '#F3F4F6', display: 'flex', marginBottom: '10px' }}>
                    <div style={{ width: `${(cat.dealer_stock / maxCat) * 100}%`, background: '#7C3AED', borderRadius: '4px 0 0 4px', transition: 'width 0.5s' }} />
                    <div style={{ width: `${(cat.installed / maxCat) * 100}%`, background: '#059669', transition: 'width 0.5s' }} />
                  </div>
                  <div style={{ display: 'flex', gap: '12px', fontSize: '11px' }}>
                    <span style={{ color: '#7C3AED', fontWeight: 600 }}>Stock: {cat.dealer_stock}</span>
                    <span style={{ color: '#059669', fontWeight: 600 }}>Installed: {cat.installed}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Top clients + Recent activity ──────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>

        <div style={{ background: C.white, borderRadius: '16px', border: `1px solid ${C.border}`, padding: '24px' }}>
          <div style={{ fontWeight: 700, fontSize: '15px', color: C.textMain, marginBottom: '20px' }}>🏆 Top Clients by Revenue</div>
          {topC.length === 0
            ? <div style={{ fontSize: '13px', color: C.textSub, textAlign: 'center', padding: '24px 0' }}>No revenue data yet</div>
            : topC.map((c, i) => (
              <div key={c.name} style={{ marginBottom: '14px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                      width: '22px', height: '22px', borderRadius: '50%',
                      background: COLORS[i % COLORS.length], color: '#fff',
                      fontSize: '10px', fontWeight: 800,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>{i + 1}</div>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: C.textMain }}>{c.name}</span>
                  </div>
                  <span style={{ fontSize: '13px', fontWeight: 700, color: COLORS[i % COLORS.length] }}>{fmtK(c.amount)}</span>
                </div>
                <div style={{ height: '5px', borderRadius: '3px', background: '#F3F4F6' }}>
                  <div style={{
                    height: '100%', borderRadius: '3px',
                    width: `${(c.amount / maxAmt) * 100}%`,
                    background: COLORS[i % COLORS.length],
                    transition: 'width 0.6s ease',
                  }} />
                </div>
              </div>
            ))
          }
        </div>

        <div style={{ background: C.white, borderRadius: '16px', border: `1px solid ${C.border}`, padding: '24px' }}>
          <div style={{ fontWeight: 700, fontSize: '15px', color: C.textMain, marginBottom: '20px' }}>⚡ Recent Activity</div>
          {recent.length === 0
            ? <div style={{ fontSize: '13px', color: C.textSub, textAlign: 'center', padding: '24px 0' }}>No recent activity</div>
            : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {recent.map((r, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'flex-start', gap: '12px',
                    padding: '10px 12px', borderRadius: '10px', background: r.color || '#F9FAFB',
                  }}>
                    <span style={{ fontSize: '18px', flexShrink: 0 }}>{r.icon}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: C.textMain, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.text}</div>
                      <div style={{ fontSize: '11px', color: C.textSub }}>{r.sub}</div>
                    </div>
                    <div style={{ fontSize: '10px', color: C.textSub, whiteSpace: 'nowrap', flexShrink: 0 }}>{r.time}</div>
                  </div>
                ))}
              </div>
            )
          }
        </div>
      </div>

    </DealerLayout>
  );
}
